import {
  AdditiveBlending,
  BufferAttribute,
  Group,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  NormalBlending,
  ShaderMaterial,
  type Blending,
} from 'three';
import type { GameplayWaveState, GameplayWaveStyle, Rgb } from '../../visual/index.js';
import { NOISE_GLSL } from '../visual/materials/InstancedField.js';

/**
 * The gameplay wave, drawn as something other than a mandala.
 *
 * ## Why this is a separate object from the mandala path
 *
 * MANDALA is not implemented here at all. `RippleField`'s chart shader keeps
 * drawing it, untouched, exactly as it did before this file existed — which is
 * the only way to be certain the regression baseline in §5 and §36 actually
 * holds. A wave whose style is MANDALA goes down the old path; a wave with any
 * other style is skipped there and drawn here instead. Nothing was rewritten to
 * make room, and reverting to the old behaviour is a matter of the director
 * answering MANDALA to everything.
 *
 * ## One mesh per style
 *
 * The alternative — a single shader branching on a style index — was rejected.
 * Seven materials in one fragment shader means every pixel of every wave pays
 * for the branch tests of six styles it is not, and these are the objects that
 * are on screen continuously while somebody is trying to play. Separate
 * materials cost one draw call per style *in use*: a FIXED recipe draws one,
 * and the worst case a SHUFFLE can reach is seven.
 *
 * Instances are pooled per style and the count is set per frame, so a style
 * nobody selected costs nothing beyond the allocation.
 *
 * ## What the shaders may read
 *
 * `aWaveA/B/C` and nothing else. Position and radius arrive from the simulation
 * and are written verbatim — a style may deform its own material inside the
 * quad, but the quad is where the simulation put it. That is §19, and it is
 * enforced by the vertex shader having no other source of position.
 */

/** Instance slots per style. A chart with more concurrent waves than this
 *  simply draws the first `capacity` of them, as the mandala path does. */
const DEFAULT_CAPACITY = 48;

export interface GameplayWaveFieldOptions {
  readonly fieldRadius: number;
  readonly capacity?: number;
}

/** The non-mandala styles, in the order their meshes are built. */
const DRAWN_STYLES = [
  'RIPPLE', 'SMOKE', 'CODE', 'MATH', 'SUMI', 'WATERCOLOR', 'INK_PAINT',
] as const;
type DrawnStyle = (typeof DRAWN_STYLES)[number];

interface StyleMesh {
  readonly style: DrawnStyle;
  readonly mesh: Mesh;
  readonly geometry: InstancedBufferGeometry;
  readonly material: ShaderMaterial;
  readonly a: Float32Array;
  readonly b: Float32Array;
  readonly c: Float32Array;
  readonly d: Float32Array;
  readonly attributes: InstancedBufferAttribute[];
  count: number;
}

export class GameplayWaveField {
  readonly object: Group;
  readonly capacity: number;

  private readonly meshes = new Map<DrawnStyle, StyleMesh>();
  private readonly fieldRadius: number;

  constructor(options: GameplayWaveFieldOptions) {
    this.fieldRadius = options.fieldRadius;
    this.capacity = Math.max(1, options.capacity ?? DEFAULT_CAPACITY);
    this.object = new Group();
    this.object.name = 'gameplay:waves';
    // Drawn after the seals and the core, before the HUD. Gameplay waves are
    // the foreground of the playfield.
    this.object.renderOrder = 6;

    for (const style of DRAWN_STYLES) {
      const built = this.build(style);
      this.meshes.set(style, built);
      this.object.add(built.mesh);
    }
  }

  /** Song time, for materials that breathe. Never a wall clock. */
  setTime(seconds: number): void {
    for (const entry of this.meshes.values()) {
      const uniform = entry.material.uniforms.uTime;
      if (uniform !== undefined) uniform.value = seconds;
    }
  }

  /** Starts a frame: every style's instance count returns to zero. */
  begin(): void {
    for (const entry of this.meshes.values()) entry.count = 0;
  }

  /**
   * Adds one wave to its style's buffer.
   *
   * Returns false when the style is MANDALA (drawn elsewhere) or the pool for
   * that style is full, so the caller can account for what it did not draw.
   */
  /**
   * Adds one wave, in one colour.
   *
   * The pigment arrives per instance rather than as a uniform because a single
   * draw carries every wave of that style, and §6 asks for wave 1 red and wave 2
   * blue on screen together. One uniform would make a style a single colour for
   * the whole frame.
   *
   * Only the pigment materials read it. The others are handed it and ignore it,
   * which is deliberate — see the note on `FRAGMENTS`.
   */
  write(style: GameplayWaveStyle, wave: GameplayWaveState, pigment?: Rgb): boolean {
    if (style === 'MANDALA') return false;
    const entry = this.meshes.get(style as DrawnStyle);
    if (entry === undefined) return false;
    const slot = entry.count;
    if (slot >= this.capacity) return false;

    const base = slot * 4;
    // A: where it is and how far along. Straight from the simulation.
    entry.a[base] = wave.x;
    entry.a[base + 1] = wave.y;
    entry.a[base + 2] = wave.distance;
    entry.a[base + 3] = wave.progress;

    // B: how it should look right now.
    entry.b[base] = wave.strength;
    entry.b[base + 1] = wave.seed % 4096;
    entry.b[base + 2] = outcomeCode(wave);
    entry.b[base + 3] = wave.life;

    // C: the rest of the state a material might key off.
    entry.c[base] = wave.direction;
    entry.c[base + 1] = wave.lifetime;
    entry.c[base + 2] = wave.resolve;
    entry.c[base + 3] = wave.badFlash + (wave.warning ? 2 : 0);

    // D: the pigment. Falls back to the wave's own authored colour when no
    // palette was supplied, so a caller that knows nothing about palettes still
    // gets something reasonable rather than black.
    entry.d[base] = pigment === undefined ? wave.red : (pigment[0] as number);
    entry.d[base + 1] = pigment === undefined ? wave.green : (pigment[1] as number);
    entry.d[base + 2] = pigment === undefined ? wave.blue : (pigment[2] as number);
    entry.d[base + 3] = 1;

    entry.count = slot + 1;
    return true;
  }

  /** Ends a frame: uploads whatever was written and sets the draw counts. */
  commit(): void {
    for (const entry of this.meshes.values()) {
      entry.geometry.instanceCount = entry.count;
      entry.mesh.visible = entry.count > 0;
      if (entry.count === 0) continue;
      for (const attribute of entry.attributes) {
        attribute.needsUpdate = true;
        // Only the slots in use are re-uploaded. A full-buffer upload every
        // frame would move 48 instances' worth of floats for the two or three
        // waves usually alive.
        attribute.updateRanges.length = 0;
        attribute.addUpdateRange(0, entry.count * 4);
      }
    }
  }

  /** Blanks every style, e.g. when a song is unloaded. */
  clear(): void {
    this.begin();
    this.commit();
  }

  /** How many instances each style drew last frame. For the report and tests. */
  counts(): Readonly<Record<string, number>> {
    const out: Record<string, number> = {};
    for (const [style, entry] of this.meshes) out[style] = entry.count;
    return out;
  }

  /** Draw calls this field will issue this frame. */
  get activeDrawCalls(): number {
    let total = 0;
    for (const entry of this.meshes.values()) if (entry.count > 0) total += 1;
    return total;
  }

  dispose(): void {
    for (const entry of this.meshes.values()) {
      entry.geometry.dispose();
      entry.material.dispose();
    }
    this.meshes.clear();
    this.object.clear();
  }

  private build(style: DrawnStyle): StyleMesh {
    const count = this.capacity;
    const geometry = buildQuad();
    geometry.instanceCount = 0;

    const a = new Float32Array(count * 4);
    const b = new Float32Array(count * 4);
    const c = new Float32Array(count * 4);
    const d = new Float32Array(count * 4);
    const attributes: InstancedBufferAttribute[] = [];
    const bind = (name: string, data: Float32Array): void => {
      const attribute = new InstancedBufferAttribute(data, 4);
      attribute.setUsage(35048 /* DynamicDrawUsage */);
      geometry.setAttribute(name, attribute);
      attributes.push(attribute);
    };
    bind('aWaveA', a);
    bind('aWaveB', b);
    bind('aWaveC', c);
    bind('aWaveD', d);

    const material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uFieldRadius: { value: this.fieldRadius },
        uQuadScale: { value: QUAD_SCALE[style] },
      },
      vertexShader: VERTEX,
      fragmentShader: `${NOISE_GLSL}\n${COMMON}\n${FRAGMENTS[style]}`,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: BLENDING[style],
    });

    const mesh = new Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.name = `gameplay:${style}`;
    mesh.visible = false;
    return { style, mesh, geometry, material, a, b, c, d, attributes, count: 0 };
  }
}

function outcomeCode(wave: GameplayWaveState): number {
  switch (wave.outcome) {
    case 'hit': return 1;
    case 'safe': return 2;
    case 'miss': return 3;
    default: return 0;
  }
}

function buildQuad(): InstancedBufferGeometry {
  const corners = new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]);
  const geometry = new InstancedBufferGeometry();
  geometry.setAttribute('aCorner', new BufferAttribute(corners, 2));
  geometry.setIndex(new BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
  return geometry;
}

/**
 * How much room each style needs around its wave radius.
 *
 * Not one number for all of them, because they do not occupy their quads the
 * same way. A ripple is a thin ring at the radius and needs barely any margin;
 * smoke is a turbulent mass that drifts well past its nominal edge, and clipping
 * it at the quad boundary produces the straight-edged cut Phase 6A spent a long
 * time removing from SUMI. These are the multipliers that keep each material
 * inside its own quad.
 */
const QUAD_SCALE: Readonly<Record<DrawnStyle, number>> = {
  RIPPLE: 1.35,
  SMOKE: 2.1,
  CODE: 1.5,
  MATH: 1.5,
  SUMI: 1.9,
  WATERCOLOR: 1.8,
  INK_PAINT: 1.9,
};

/**
 * Additive for the styles that read as light, normal for the ones that read as
 * pigment. The same split Phase 6A settled on: watercolour and ink turned into
 * white blobs under bloom when they were additive, because a wash that is
 * brighter where it overlaps is not a wash.
 */
const BLENDING: Readonly<Record<DrawnStyle, Blending>> = {
  RIPPLE: AdditiveBlending,
  SMOKE: NormalBlending,
  CODE: AdditiveBlending,
  MATH: AdditiveBlending,
  SUMI: NormalBlending,
  WATERCOLOR: NormalBlending,
  INK_PAINT: NormalBlending,
};

/**
 * One vertex shader for every style.
 *
 * The quad is centred on the wave's simulation origin and sized from its
 * simulation radius. There is no other input — a style cannot move itself,
 * because there is nowhere for it to write a position.
 */
const VERTEX = /* glsl */ `
attribute vec2 aCorner;
attribute vec4 aWaveA;
attribute vec4 aWaveB;
attribute vec4 aWaveC;
attribute vec4 aWaveD;

uniform float uQuadScale;

varying vec2 vLocal;
varying vec4 vA;
varying vec4 vB;
varying vec4 vC;
varying vec4 vD;

void main() {
  vLocal = aCorner;
  vA = aWaveA;
  vB = aWaveB;
  vC = aWaveC;
  vD = aWaveD;

  // A wave still forming at its origin has no radius yet, so it needs a size of
  // its own or it would be invisible until the moment it starts expanding —
  // which is exactly the advance warning the player is owed.
  float radius = max(aWaveA.z, 0.35);
  // Not named half: that is a reserved word in GLSL, and naming it so made every
  // one of these shaders fail to compile — silently, because a failed program
  // draws nothing rather than raising anything the render loop can see.
  float extent = radius * uQuadScale;
  vec2 world = aWaveA.xy + aCorner * extent;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 0.0, 1.0);
}
`;

/**
 * Shared helpers.
 *
 * `vLocal` is the quad in [-1,1]; `unit` rescales it so that 1.0 is the wave's
 * actual simulation radius rather than the quad edge. Every style measures from
 * `unit`, which is what keeps them all agreeing about where the wave *is* even
 * though they use different amounts of the quad around it.
 */
const COMMON = /* glsl */ `
precision highp float;

varying vec2 vLocal;
varying vec4 vA;
varying vec4 vB;
varying vec4 vC;
varying vec4 vD;

uniform float uTime;
uniform float uFieldRadius;
uniform float uQuadScale;

// The shared fbm takes an octave count; four is what these materials use.
float fbm(vec2 p) { return fbm(p, 4); }

float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

/** The quad, rescaled so |p| == 1 is the wave's radius. */
vec2 unitPos() { return vLocal * uQuadScale; }

/** Outcome: 0 pending, 1 hit, 2 safe, 3 miss. */
float outcome() { return vB.z; }
float resolveAmount() { return vC.z; }
float badFlash() { return min(vC.w, 1.0); }
bool isWarning() { return vC.w >= 2.0; }
float waveSeed() { return vB.y; }
float strength() { return vB.x; }
float progress() { return vA.w; }
float life() { return vB.w; }
// The palette pigment this wave was given. See GameplayWaveField.write.
vec3 pigment() { return vD.rgb; }
`;

// ---------------------------------------------------------------------------
// The styles
// ---------------------------------------------------------------------------

const FRAG_RIPPLE = /* glsl */ `
/**
 * A propagating wave front, not a ring.
 *
 * The distinction is the whole of §6. A ring is a circle of constant radius; a
 * front is a disturbance with a leading edge, a wake behind it, and a phase that
 * keeps moving through it. So this draws a packet: a primary crest at the wave's
 * own radius, trailing crests behind at decreasing amplitude, and a secondary
 * front launched a beat later that interferes with the first.
 *
 * Everything grows with approach rather than with time, so the packet tightens
 * and steepens as the moment arrives — which is what makes it read as coming
 * *at* the player rather than merely expanding.
 */
void main() {
  vec2 p = unitPos();
  float r = length(p);
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.4);

  // The front sharpens as it approaches: a distant wave is a broad swell, a
  // near one is a hard edge.
  float sharpness = mix(6.0, 26.0, clamp(approach, 0.0, 1.0));
  float phase = (1.0 - r) * sharpness + uTime * 3.0 + hash11(seed) * 6.28;

  // Primary packet, centred on the simulation radius.
  float envelope = exp(-pow(abs(r - 1.0) * mix(2.6, 5.5, approach), 2.0));
  float crest = sin(phase);
  float primary = envelope * (0.5 + 0.5 * crest);

  // A secondary front, behind and slower. Where the two meet they add and
  // cancel, which is the interference the material is supposed to show.
  float secondaryR = 1.0 - 0.34 - 0.1 * sin(uTime * 1.7 + seed);
  float envelope2 = exp(-pow(abs(r - secondaryR) * 4.2, 2.0));
  float crest2 = sin(phase * 0.72 + 2.1);
  float secondary = envelope2 * (0.5 + 0.5 * crest2) * 0.55;

  float field = primary + secondary;

  // Deformation: the front is not a perfect circle. Low-order angular modes,
  // weighted by strength, so a loud wave arrives visibly distorted.
  float angle = atan(p.y, p.x);
  float deform = 0.06 * strength() * (sin(angle * 3.0 + seed) + 0.6 * sin(angle * 5.0 - uTime));
  field *= 1.0 + deform * 6.0;

  // Thickness evolves: thin far away, thick and heavy on arrival.
  field *= mix(0.55, 1.25, approach);

  // Held under one before anything else sees it. The primary and secondary
  // packets sum, the deformation multiplies, and the total was reaching about
  // 2.4 — which additive blending turns into a white disc the moment bloom
  // gets hold of it, and every crest and node inside it is lost. The whole
  // point of a wave packet is the structure, so the structure is what has to
  // survive the exposure.
  field = clamp(field, 0.0, 1.0) * 0.62;

  vec3 colour = mix(vec3(0.22, 0.62, 0.85), vec3(0.55, 0.80, 0.95), primary);

  float alpha = field * life();

  // HIT collapses the packet inward and brightens; MISS scatters it.
  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      alpha *= (1.0 - res);
      colour += vec3(0.4, 0.5, 0.3) * (1.0 - res) * res * 4.0;
    } else {
      float shatter = 0.5 + 0.5 * sin(angle * 9.0 + seed * 3.0);
      alpha *= (1.0 - res) * mix(1.0, shatter, res);
      colour = mix(colour, vec3(1.0, 0.35, 0.45), res);
    }
  }
  alpha *= 1.0 + badFlash() * 1.5;
  if (isWarning()) alpha *= 0.45;

  if (alpha <= 0.002) discard;
  gl_FragColor = vec4(colour * alpha, alpha);
}
`;

const FRAG_SMOKE = /* glsl */ `
/**
 * A smoke body approaching, not smoke in the background.
 *
 * §7 is explicit that placing the existing background smoke behind the
 * playfield does not count. What makes this a gameplay object is that its
 * density is a function of approach: it arrives as a thin distant mass, gathers
 * opacity and internal contrast as it nears, and develops shear — the leading
 * edge outruns the body — so it reads as travelling rather than as sitting
 * still and thickening.
 */
void main() {
  vec2 p = unitPos();
  float r = length(p);
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  // Shear: the flow direction leans with the gate direction, and the leading
  // half is dragged forward relative to the trailing half.
  vec2 flow = vec2(cos(vC.x), sin(vC.x));
  float lead = dot(normalize(p + 1e-5), flow);
  vec2 sheared = p - flow * lead * 0.22 * approach;

  // Turbulence, scrolling against the direction of travel so the body looks
  // like it is being pushed through air rather than rotating.
  vec2 q = sheared * 1.6 + flow * uTime * 0.35 + seed;
  float n = fbm(q);
  n += 0.5 * fbm(q * 2.3 - flow * uTime * 0.5);

  // A shell rather than a disc: the mass is concentrated near the wave radius
  // and hollow behind it, which is what stops it looking like a cloud pinned to
  // the origin.
  float shell = exp(-pow(abs(r - 0.86) * 2.3, 2.0));
  float body = shell * (0.35 + 0.65 * n);

  // Density climbs with approach, and the internal contrast climbs with it.
  float density = body * mix(0.35, 1.15, approach);
  density *= mix(1.0, 1.0 + 0.8 * (n - 0.5), approach);

  // Kept below the bloom threshold of 0.55. Smoke lit brighter than that stops
  // being smoke and becomes a lamp: the turbulence is still there in the alpha
  // but every value clips to the same white and none of it is visible.
  vec3 colour = mix(vec3(0.13, 0.16, 0.21), vec3(0.38, 0.42, 0.49), n);
  colour = mix(colour, vec3(0.50, 0.46, 0.40), strength() * 0.3);

  float alpha = clamp(density, 0.0, 1.0) * life() * 0.72;

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: a coherent collapse — the mass contracts and fades as one body.
      float contract = 1.0 - res * 0.7;
      alpha *= smoothstep(0.0, 0.2, contract - abs(r - 0.86)) * (1.0 - res);
    } else {
      // MISS: turbulent dispersion — the same noise, violently expanded.
      float scatter = fbm(q * (1.0 + res * 4.0));
      alpha *= (1.0 - res) * (0.4 + 0.9 * scatter);
      colour = mix(colour, vec3(0.45, 0.16, 0.18), res * 0.8);
    }
  }
  alpha *= 1.0 + badFlash();
  if (isWarning()) alpha *= 0.4;

  if (alpha <= 0.003) discard;
  gl_FragColor = vec4(colour, alpha);
}
`;

const FRAG_CODE = /* glsl */ `
/**
 * A cluster of glyphs on its way in.
 *
 * Drawn as a lattice rather than as text: real typography would need the atlas
 * the mandala seal uses, and a gameplay wave changes size every frame, so the
 * glyphs would be unreadable at exactly the moments they mattered. What is
 * legible at speed is the *structure* — a grid of lit cells with holes in it,
 * scan lines, and fragments that break off — so that is what this draws.
 *
 * The lattice is seeded from the wave, not from time, so a given wave carries
 * the same arrangement every frame of its approach and reads as one object.
 */
void main() {
  vec2 p = unitPos();
  float r = length(p);
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  // Cells tighten as the wave nears, so the cluster resolves into finer detail
  // rather than simply scaling up.
  float cells = mix(5.0, 13.0, clamp(approach, 0.0, 1.0));
  vec2 grid = p * cells;
  vec2 cell = floor(grid);
  vec2 inCell = fract(grid) - 0.5;

  float pick = hash21(cell + seed);
  // Only some cells are lit, and which ones is fixed per wave.
  float lit = step(0.42, pick);

  // Inside a lit cell, a short bar — the glyph stand-in. Its orientation comes
  // from the cell hash so the cluster has texture rather than uniformity.
  float horizontal = step(0.5, hash21(cell * 1.7 + seed));
  vec2 bar = mix(vec2(0.34, 0.08), vec2(0.08, 0.34), horizontal);
  float glyph = step(abs(inCell.x), bar.x) * step(abs(inCell.y), bar.y);

  // The cluster is a shell around the wave radius, thinning outward.
  float shell = exp(-pow(abs(r - 0.9) * 2.6, 2.0));

  // A scan line travelling outward with the front.
  float scan = exp(-pow((r - fract(uTime * 0.6 + hash11(seed))) * 14.0, 2.0));

  float field = lit * glyph * shell + scan * 0.35 * shell;
  field *= mix(0.5, 1.2, approach);
  // Additive, so overlapping cells sum. Scaled so a cluster reads as lit glyphs
  // rather than as one solid block.
  field = clamp(field, 0.0, 1.0) * 0.7;

  vec3 colour = mix(vec3(0.18, 0.78, 0.55), vec3(0.55, 0.92, 0.75), glyph * lit);

  float alpha = field * life();

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: clean fragmentation — cells fly outward intact.
      float push = res * 0.35;
      float moved = exp(-pow(abs(r - 0.9 - push) * 2.6, 2.0));
      alpha = lit * glyph * moved * (1.0 - res) * life();
    } else {
      // MISS: corrupted break — cells scramble and the colour goes wrong.
      float corrupt = hash21(cell * (1.0 + res * 9.0) + seed);
      alpha *= (1.0 - res) * step(0.3, corrupt);
      colour = mix(colour, vec3(1.0, 0.25, 0.35), res);
    }
  }
  alpha *= 1.0 + badFlash() * 2.0;
  if (isWarning()) alpha *= 0.4;

  if (alpha <= 0.003) discard;
  gl_FragColor = vec4(colour * alpha, alpha);
}
`;

const FRAG_MATH = /* glsl */ `
/**
 * Notation approaching: rings, ticks and radial marks.
 *
 * Kept visually distinct from CODE, which is the requirement in §9. CODE is a
 * rectilinear lattice of filled cells; this is *circular* — concentric rings
 * divided into graduated segments, with radial spokes and a rotating index
 * mark, the vocabulary of a dial or a plotted function rather than a grid.
 */
void main() {
  vec2 p = unitPos();
  float r = length(p);
  float angle = atan(p.y, p.x);
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  // Concentric rings. The count is per-wave, so no two waves are the same dial.
  float ringCount = 3.0 + floor(hash11(seed) * 3.0);
  float rings = abs(fract(r * ringCount) - 0.5) * 2.0;
  float ringLine = smoothstep(0.86, 1.0, rings);

  // Graduations: short ticks around each ring, denser as the wave nears.
  float ticks = 24.0 + floor(approach * 24.0);
  float tick = abs(fract(angle / 6.2831853 * ticks) - 0.5) * 2.0;
  float tickLine = smoothstep(0.82, 1.0, tick) * step(0.55, r) * step(r, 1.05);

  // Radial spokes, few and fixed per wave.
  float spokes = 4.0 + floor(hash11(seed * 1.7) * 5.0);
  float spoke = abs(fract(angle / 6.2831853 * spokes + 0.5) - 0.5) * 2.0;
  float spokeLine = smoothstep(0.94, 1.0, spoke) * smoothstep(1.05, 0.35, r);

  // An index mark sweeping the dial, so the object has a readable "now".
  float index = exp(-pow(mod(angle - uTime * 1.1 + 9.4248, 6.2831853) * 3.0, 2.0));

  float shell = exp(-pow(abs(r - 0.92) * 2.2, 2.0));
  float field = (ringLine * 0.7 + tickLine * 0.6 + spokeLine * 0.45) * shell
    + index * shell * 0.5;
  field *= mix(0.45, 1.15, approach);
  field = clamp(field, 0.0, 1.0) * 0.72;

  // Amber against CODE's green: the two share a vocabulary of lit marks on a
  // dark field, so hue is what has to separate them at a glance.
  vec3 colour = mix(vec3(0.85, 0.62, 0.20), vec3(0.95, 0.82, 0.52), ringLine);

  float alpha = field * life();

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: the dial closes — rings converge on the centre.
      float closed = exp(-pow(abs(r - 0.92 * (1.0 - res)) * 2.2, 2.0));
      alpha = (ringLine * 0.7 + spokeLine * 0.45) * closed * (1.0 - res) * life();
    } else {
      // MISS: graduations skew and the dial falls apart.
      float skew = sin(angle * 7.0 + res * 20.0) * res;
      alpha *= (1.0 - res) * (0.5 + 0.5 * abs(skew));
      colour = mix(colour, vec3(1.0, 0.3, 0.2), res);
    }
  }
  alpha *= 1.0 + badFlash() * 1.5;
  if (isWarning()) alpha *= 0.4;

  if (alpha <= 0.003) discard;
  gl_FragColor = vec4(colour * alpha, alpha);
}
`;

const FRAG_SUMI = /* glsl */ `
/**
 * A brush stroke arriving.
 *
 * ## What must not come back
 *
 * Phase 6A removed three specific defects from the sumi material and §10 names
 * all three. There is **no paper plane** — the quad is transparent everywhere
 * the ink is not, so nothing white sits behind the stroke. There is **no white
 * halo** — dilute ink is drawn *darker and thinner*, never brighter than the
 * core, because ink diluted with water is weaker, not luminous. And there is
 * **no rectangular clipping** — the stroke and its bleed are contained by
 * construction: the distance field is divided by a containment term that grows
 * with the feathering, so heavier bleed shrinks the stroke rather than letting
 * it reach the quad edge.
 */
void main() {
  vec2 p = unitPos();
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  // The stroke runs across the wave, oriented by the gate direction.
  float a = vC.x;
  vec2 axis = vec2(cos(a), sin(a));
  vec2 across = vec2(-axis.y, axis.x);
  float along = dot(p, axis) * 0.5 + 0.5;
  float lateral = dot(p, across);

  // Pressure: light at the start, heaviest in the middle, lifting at the end.
  float attack = smoothstep(0.0, 0.10, along);
  float release = 1.0 - smoothstep(0.58, 0.98, along);
  float pressure = attack * pow(max(release, 0.0), 0.5);

  // A curved spine, so the stroke is a gesture rather than a bar.
  float bend = (fract(seed * 0.317) - 0.5) * 0.9;
  float spine = bend * (along * (1.0 - along) * 4.0 - 0.5);

  // Width grows with approach: the brush loads as it comes.
  float width = mix(0.10, 0.26, approach) * pressure;
  float d = abs(lateral - spine) / max(width, 0.0006);

  // Fibre texture, and the wicking of ink into fibre at the edges.
  float fibre = fbm(vec2(along * 7.0, lateral * 11.0) + seed);
  float wick = 0.16 + 0.24 * fibre;
  d += (fibre - 0.5) * 0.5 * wick;

  // Containment. The boundary is pushed *in* by however much the wicking
  // pushed it out, so no amount of feathering reaches the quad edge. This is
  // the fix from Phase 6A and the reason the stroke never squares off.
  float contain = (1.0 + wick * 0.4) / max(0.94 - wick * 0.5, 0.3);
  d *= contain;

  float core = 1.0 - smoothstep(0.55, 1.0, d);
  float bleed = (1.0 - smoothstep(0.9, 2.1, d)) * wick;

  // Dilute ink is *dimmer*, never brighter. Inverting this is what produced the
  // white halo, and it is worth stating rather than only avoiding.
  float ink = core + bleed * 0.35;
  ink *= mix(0.5, 1.0, approach);

  float tone = mix(0.10, 0.02, core);
  vec3 colour = vec3(tone);

  float alpha = clamp(ink, 0.0, 1.0) * life();

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: a controlled finish — the stroke completes and bleeds gently.
      alpha *= (1.0 - res);
      alpha += core * (1.0 - res) * res * 0.5;
    } else {
      // MISS: fractured splatter — the stroke breaks into droplets.
      float drops = fbm(p * (6.0 + res * 14.0) + seed);
      alpha *= (1.0 - res) * step(0.42, drops);
    }
  }
  alpha *= 1.0 + badFlash();
  if (isWarning()) alpha *= 0.35;

  if (alpha <= 0.004) discard;
  gl_FragColor = vec4(colour, alpha);
}
`;

const FRAG_WATERCOLOR = /* glsl */ `
/**
 * A pigment wash blooming toward the player.
 *
 * §11 forbids a translucent circle, which is what this would be without the
 * three things that make watercolour recognisable: an **irregular lobed
 * boundary** where the water ran, **edge darkening** where pigment collected as
 * the wash dried, and **granulation** where it settled into the paper's grain.
 * All three are here, and all three strengthen with approach, so the wash looks
 * increasingly dry — which is how it reads as having travelled.
 */
void main() {
  vec2 p = unitPos();
  float r = length(p);
  float angle = atan(p.y, p.x);
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  // Dryness drives the character. A young wash is soft and even; an old one has
  // a hard rim and visible grain.
  float dryness = clamp(approach, 0.0, 1.0);

  // A lobed boundary rather than a circle: several angular modes, seeded.
  float lobes =
    0.13 * sin(angle * 3.0 + seed) +
    0.08 * sin(angle * 5.0 - seed * 1.7) +
    0.05 * sin(angle * 8.0 + seed * 0.6);
  float edge = 0.92 + lobes * (0.5 + 0.5 * dryness);

  // The wash body, growing asymptotically — water spreads fast then stops.
  float spread = 1.0 - exp(-approach * 2.2);
  float boundary = edge * mix(0.45, 1.0, spread);

  float wash = 1.0 - smoothstep(boundary - 0.22, boundary, r);
  if (wash <= 0.0) discard;

  // Edge darkening: pigment carried to the rim by the retreating water.
  float rim = exp(-pow((r - boundary) * 9.0, 2.0));
  float edgeDark = rim * mix(0.25, 0.95, dryness);

  // Granulation: pigment settling into grain, stronger as it dries.
  float grain = fbm(p * 5.5 + seed) * fbm(p * 13.0 - seed * 0.3);
  float granulation = (grain - 0.45) * mix(0.15, 0.65, dryness);

  // Wet-on-wet: soft internal variation that never reaches the boundary.
  float bloom = fbm(p * 2.4 + uTime * 0.12 + seed) * (1.0 - dryness) * 0.4;

  float density = wash * (0.42 + granulation + bloom) + edgeDark * 0.55;

  // The palette entry this wave was given, thinned. A wash is water carrying
  // pigment, so it takes the same colour ink would and dilutes it: pulled a
  // little towards its own grey and scaled down. Put the same red through both
  // and this is the one you can see the ground through.
  vec3 base = pigment();
  float grey = (base.r + base.g + base.b) / 3.0;
  vec3 thinned = mix(vec3(grey), base, 0.88) * 1.35;
  // Where pigment gathers it is *deeper*, not brighter.
  vec3 colour = thinned * (1.0 - 0.45 * edgeDark);

  float alpha = clamp(density, 0.0, 1.0) * life() * 0.8;

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: the wash settles — it darkens at the rim and sinks in.
      alpha *= (1.0 - res * 0.85);
      colour *= 1.0 - res * 0.3;
    } else {
      // MISS: it runs — the boundary breaks and pigment streaks outward.
      float run = fbm(vec2(angle * 3.0, r * 6.0 - res * 5.0) + seed);
      alpha *= (1.0 - res) * (0.35 + 0.9 * run);
    }
  }
  alpha *= 1.0 + badFlash() * 0.8;
  if (isWarning()) alpha *= 0.35;

  if (alpha <= 0.004) discard;
  gl_FragColor = vec4(colour, alpha);
}
`;

const FRAG_INK_PAINT = /* glsl */ `
/**
 * An ink mass travelling, with a drag tail behind it.
 *
 * §12 asks for this to be kept apart from the persistent paint. It is: this
 * draws the *projectile* — a splat body with a directional drag trail — and
 * nothing here writes to the paint target. Whether an impact leaves a stain is
 * the persistent layer's decision, made from the same judged event, which keeps
 * the two loosely coupled rather than one being a side effect of the other.
 *
 * The splat body is deliberately not a circle, an ellipse or a rectangle: the
 * radius is modulated by several angular modes plus noise, so its silhouette
 * reads as something thrown.
 */
void main() {
  vec2 p = unitPos();
  float seed = waveSeed();
  float approach = clamp(progress(), 0.0, 1.3);

  float a = vC.x;
  vec2 dir = vec2(cos(a), sin(a));

  // Drag: the body is offset backwards along its direction of travel and a tail
  // stretches behind it, lengthening with approach.
  float drag = 0.28 * approach;
  vec2 body = p + dir * drag * 0.5;
  float r = length(body);
  float angle = atan(body.y, body.x);

  // A lumpy silhouette. Three modes plus noise — nothing here can round off
  // into a circle or flatten into an ellipse.
  float lumps =
    0.16 * sin(angle * 3.0 + seed * 1.1) +
    0.10 * sin(angle * 5.0 - seed * 0.7) +
    0.07 * sin(angle * 7.0 + seed * 2.3);
  float grain = (fbm(body * 4.0 + seed) - 0.5) * 0.22;
  float edge = (0.62 + lumps + grain) * mix(0.55, 1.0, approach);

  float mass = 1.0 - smoothstep(edge - 0.10, edge, r);

  // The tail: a narrowing streak behind the body along the travel direction.
  float behind = dot(p, dir);
  float lateral = abs(dot(p, vec2(-dir.y, dir.x)));
  float tailWidth = mix(0.30, 0.10, clamp(behind * 1.2, 0.0, 1.0));
  float tail = step(0.0, behind) * (1.0 - smoothstep(0.0, tailWidth, lateral))
    * (1.0 - smoothstep(0.2, 1.5, behind)) * 0.6 * approach;

  // A few flung droplets, fixed per wave.
  float drops = 0.0;
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    vec2 at = dir * (0.9 + fi * 0.35) + vec2(hash11(seed + fi) - 0.5, hash11(seed + fi * 3.1) - 0.5) * 0.7;
    drops += (1.0 - smoothstep(0.0, 0.10 + 0.04 * fi, length(p - at))) * 0.7;
  }

  float ink = clamp(mass + tail + drops * approach, 0.0, 1.0);

  // Nearly black, and opaque where the body is. The distinction from
  // WATERCOLOR is exactly this: a wash is transparent pigment that the field
  // shows through, ink is a mass that covers. At the first attempt both came
  // out the same mauve because this was too light and too thin to read as ink.
  // The palette entry at full strength, with the grain darkening it rather than
  // tinting it. Ink covers: it is the same pigment the wash carries, laid down
  // dense instead of diluted.
  //
  // Held at the palette's own low value on purpose. The tone curve measured in
  // Phase 8B destroys saturation above about 0.45 input luminance — green at
  // full input came back at a saturation of 0.08 — so a brighter ink would be a
  // whiter one, which is the failure §15 exists to catch.
  vec3 base = pigment();
  vec3 colour = base * (0.72 + 0.55 * (grain + 0.5));

  float alpha = clamp(ink, 0.0, 1.0) * life() * 1.1;

  float out_ = outcome();
  float res = resolveAmount();
  if (out_ > 0.5) {
    if (out_ < 2.5) {
      // HIT: the mass lands — it flattens and sinks rather than vanishing.
      float flattened = 1.0 - smoothstep(edge * (1.0 + res * 0.4) - 0.1, edge * (1.0 + res * 0.4), r);
      alpha = flattened * (1.0 - res * 0.9) * life();
    } else {
      // MISS: it bursts — the body breaks into scattered fragments.
      float burst = fbm(body * (5.0 + res * 16.0) + seed);
      alpha *= (1.0 - res) * step(0.45, burst);
    }
  }
  alpha *= 1.0 + badFlash();
  if (isWarning()) alpha *= 0.35;

  if (alpha <= 0.004) discard;
  gl_FragColor = vec4(colour, alpha);
}
`;

const FRAGMENTS: Readonly<Record<DrawnStyle, string>> = {
  RIPPLE: FRAG_RIPPLE,
  SMOKE: FRAG_SMOKE,
  CODE: FRAG_CODE,
  MATH: FRAG_MATH,
  SUMI: FRAG_SUMI,
  WATERCOLOR: FRAG_WATERCOLOR,
  INK_PAINT: FRAG_INK_PAINT,
};
